import { createUploadRecord } from '@/api/uploadRecords';
import type { BSPPackageDetail, DocAttachment, PackageFile, ExternalReleaseDetail, ExternalReleaseFile } from '@/types';
import type { PackageFormData } from '@/components/packages/PackageForm';
import { useUploadProgressStore } from '@/store/uploadProgressStore';

const docKey = (d: DocAttachment) => (d.url ? `url:${d.url}` : `name:${d.name || ''}`);

/** 列表为空时回退到旧的单文件标量。 */
const docList = (
  files: DocAttachment[] | undefined,
  url?: string, name?: string, size?: number, checksum?: string,
): DocAttachment[] => {
  if (files?.length) return files;
  if (!url && !name) return [];
  return [{ url: url || '', name: name || '', size: size || 0, checksum: checksum || '' }];
};

/** 文档附件逐项生成上传记录条目（进度 key 带文件序号）。 */
const collectDocItems = (
  beforeDocs: DocAttachment[],
  afterDocs: DocAttachment[],
  keyPrefix: string,
  typeName: string,
  addedItems: UploadedFileItem[],
  existingItems: UploadedFileItem[],
  deletedItems: UploadedFileItem[],
) => {
  const progressStore = useUploadProgressStore.getState();
  const beforeKeys = new Set(beforeDocs.map(docKey));
  const afterKeys = new Set(afterDocs.map(docKey));

  beforeDocs.forEach((d) => {
    if (!afterKeys.has(docKey(d))) {
      deletedItems.push({
        file_name: d.name || typeName, file_size: d.size || 0,
        file_type_name: typeName, change_type: 'deleted',
      });
    }
  });
  afterDocs.forEach((d, index) => {
    const entry = progressStore.getProgress(`${keyPrefix}-${index}`);
    const item: UploadedFileItem = {
      file_name: d.name || typeName, file_size: d.size || 0, file_type_name: typeName,
      auto_retry_count: entry?.autoRetryCount ?? 0, manual_retry_count: entry?.manualRetryCount ?? 0,
      upload_started_at: entry?.uploadStartedAt ?? null, upload_finished_at: entry?.uploadFinishedAt ?? null,
      avg_speed_mbps: entry?.avgSpeedMbps ?? null,
    };
    if (beforeKeys.has(docKey(d))) {
      existingItems.push({ ...item, change_type: 'existing' });
    } else {
      addedItems.push({ ...item, change_type: 'added' });
    }
  });
};

export type UploadedFileItem = {
  file_name: string;
  file_size: number;
  file_type_name?: string;
  auto_retry_count?: number;
  manual_retry_count?: number;
  upload_started_at?: string | null;
  upload_finished_at?: string | null;
  avg_speed_mbps?: number | null;
  change_type?: 'added' | 'deleted' | 'existing' | 'failed';
};

export type ReportUploadRecordResult = {
  recordId: number | null;
  deletedItems: UploadedFileItem[];
  existingItems: UploadedFileItem[];
};

/**
 * 计算底软包编辑前后的文件变更，上报 UploadRecord。
 * 使用 `file_type_id::file_name::object_key` 作为对比键，确保同名不同内容的文件能被识别为删除+新增。
 */
export async function reportPackageUploadRecord(
  result: BSPPackageDetail,
  formData: PackageFormData,
  beforePackage: BSPPackageDetail,
  projectId: number,
  fileTypes: Array<{ id: number; name: string }>,
  startedAt?: string | null,
): Promise<ReportUploadRecordResult | null> {
  if (!result?.id) return null;

  const progressStore = useUploadProgressStore.getState();
  const addedItems: UploadedFileItem[] = [];
  const existingItems: UploadedFileItem[] = [];
  const deletedItems: UploadedFileItem[] = [];

  const beforeFiles: PackageFile[] = beforePackage.files || [];
  const beforeFileKeys = new Set<string>(
    beforeFiles.map(f => `${f.file_type?.id}::${f.file_name}::${f.object_key}`)
  );
  const afterFileKeys = new Set<string>(
    (result.files || []).map((f: any) => `${f.file_type?.id}::${f.file_name}::${f.object_key}`)
  );

  // 已删除：before 有、after 没有
  beforeFiles.forEach(f => {
    const key = `${f.file_type?.id}::${f.file_name}::${f.object_key}`;
    if (!afterFileKeys.has(key)) {
      deletedItems.push({
        file_name: f.file_name, file_size: f.file_size || 0,
        file_type_name: f.file_type?.name || '', change_type: 'deleted',
      });
    }
  });

  // 新增或已有
  (result.files || []).forEach((f: any) => {
    const file_type_name = fileTypes.find(ft => ft.id === f.file_type?.id)?.name || f.file_type?.name || '';
    const item: UploadedFileItem = {
      file_name: f.file_name, file_size: f.file_size || 0, file_type_name,
      auto_retry_count: 0, manual_retry_count: 0,
      upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
    };
    const fileKey = `${f.file_type?.id}::${f.file_name}::${f.object_key}`;
    if (!beforeFileKeys.has(fileKey)) {
      addedItems.push({ ...item, change_type: 'added' });
    } else {
      existingItems.push({ ...item, change_type: 'existing' });
    }
  });

  // 文档文件（release_note / test_report）
  const beforeRnUrl = beforePackage.release_note_url || '';
  const afterRnUrl = result.release_note_url || '';

  if (beforeRnUrl && !afterRnUrl) {
    deletedItems.push({
      file_name: beforePackage.release_note_name || 'Release Note',
      file_size: beforePackage.release_note_size || 0,
      file_type_name: 'Release Note', change_type: 'deleted',
    });
  }
  if (formData.release_note_name) {
    const rnEntry = progressStore.getProgress(`package-${result.id}-doc-release_note`);
    const rnItem: UploadedFileItem = {
      file_name: formData.release_note_name, file_size: formData.release_note_size || 0,
      file_type_name: 'Release Note',
      auto_retry_count: rnEntry?.autoRetryCount ?? 0, manual_retry_count: rnEntry?.manualRetryCount ?? 0,
      upload_started_at: rnEntry?.uploadStartedAt ?? null, upload_finished_at: rnEntry?.uploadFinishedAt ?? null,
      avg_speed_mbps: rnEntry?.avgSpeedMbps ?? null,
    };
    if (!beforeRnUrl || afterRnUrl !== beforeRnUrl) {
      addedItems.push({ ...rnItem, change_type: 'added' });
    } else {
      existingItems.push({ ...rnItem, change_type: 'existing' });
    }
  }

  collectDocItems(
    docList(
      beforePackage.test_report_files,
      beforePackage.test_report_url, beforePackage.test_report_name,
      beforePackage.test_report_size, beforePackage.test_report_checksum,
    ),
    docList(
      result.test_report_files,
      result.test_report_url, result.test_report_name,
      result.test_report_size, result.test_report_checksum,
    ),
    `package-${result.id}-doc-test_report`,
    'Test Report',
    addedItems, existingItems, deletedItems,
  );

  const allFiles = [...addedItems, ...deletedItems, ...existingItems];
  const nonDeletedFiles = allFiles.filter(f => f.change_type !== 'deleted');

  const uploadRecord = await createUploadRecord({
    project: projectId,
    deliverable_type: 'package',
    deliverable_id: result.id,
    deliverable_name: formData.name,
    vehicle_model: formData.vehicle_model,
    file_count: nonDeletedFiles.length,
    total_size: nonDeletedFiles.reduce((s, f) => s + f.file_size, 0),
    upload_status: 'uploading',
    started_at: startedAt || new Date().toISOString(),
    finished_at: undefined,
    uploaded_files: allFiles,
    description: formData.description || '',
  }).catch((err) => { console.warn('[UploadRecord] 上报失败:', err); return null; });

  return { recordId: uploadRecord?.id ?? null, deletedItems, existingItems };
}

/**
 * 为"重传"操作单独创建一条 UploadRecord（upload_type='retry'）。
 * files 中每个条目的 change_type 由调用方设置（通常目标文件为 'added'，其余失败文件为 'failed'）。
 * 返回新记录的 ID，失败时返回 null。
 */
export async function reportRetryUploadRecord(
  deliverableType: 'package' | 'external_release',
  deliverableId: number,
  projectId: number,
  deliverableMeta: { name: string; vehicleModel: string; description: string },
  files: UploadedFileItem[],
  startedAt: string,
): Promise<number | null> {
  const nonFailedFiles = files.filter(f => f.change_type !== 'failed' && f.change_type !== 'deleted');
  const uploadRecord = await createUploadRecord({
    project: projectId,
    deliverable_type: deliverableType,
    deliverable_id: deliverableId,
    deliverable_name: deliverableMeta.name,
    vehicle_model: deliverableMeta.vehicleModel,
    file_count: nonFailedFiles.length,
    total_size: nonFailedFiles.reduce((s, f) => s + f.file_size, 0),
    upload_status: 'uploading',
    started_at: startedAt,
    uploaded_files: files,
    description: deliverableMeta.description,
    upload_type: 'retry',
  }).catch(err => { console.warn('[UploadRecord] 重传记录上报失败:', err); return null; });
  return uploadRecord?.id ?? null;
}

/**
 * 计算 Momenta 对外释放文件编辑前后的文件变更，上报 UploadRecord。
 * 使用 `file_type_id::file_name::object_key` 作为对比键，确保同名不同内容的文件能被识别为删除+新增。
 */
export async function reportExternalReleaseUploadRecord(
  result: ExternalReleaseDetail,
  initialData: ExternalReleaseDetail | null | undefined,
  projectId: number,
  deliverableMeta: { name: string; vehicleModel: string; description: string },
  startedAt?: string | null,
): Promise<ReportUploadRecordResult | null> {
  if (!result?.id) return null;

  const progressStore = useUploadProgressStore.getState();
  const addedItems: UploadedFileItem[] = [];
  const existingItems: UploadedFileItem[] = [];
  const deletedItems: UploadedFileItem[] = [];

  const beforeFiles: ExternalReleaseFile[] = initialData?.files || [];
  const beforeFileKeys = new Set<string>(
    beforeFiles.map(f => `${f.file_type?.id}::${f.file_name}::${f.object_key}`)
  );
  const afterFileKeys = new Set<string>(
    (result.files || []).map(f => `${f.file_type?.id}::${f.file_name}::${f.object_key}`)
  );

  // 已删除：before 有、after 没有
  beforeFiles.forEach(f => {
    const key = `${f.file_type?.id}::${f.file_name}::${f.object_key}`;
    if (!afterFileKeys.has(key)) {
      deletedItems.push({
        file_name: f.file_name, file_size: f.file_size || 0,
        file_type_name: f.file_type?.name || '', change_type: 'deleted',
      });
    }
  });

  // 新增或已有
  (result.files || []).forEach(f => {
    const item: UploadedFileItem = {
      file_name: f.file_name, file_size: f.file_size || 0,
      file_type_name: f.file_type?.name || '',
      auto_retry_count: 0, manual_retry_count: 0,
      upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
    };
    const fileKey = `${f.file_type?.id}::${f.file_name}::${f.object_key}`;
    if (!beforeFileKeys.has(fileKey)) {
      addedItems.push({ ...item, change_type: 'added' });
    } else {
      existingItems.push({ ...item, change_type: 'existing' });
    }
  });

  // Momenta 文档（release_note / test_report）
  const beforeRnUrl = initialData?.momenta_release_note_url || '';
  const afterRnUrl = result.momenta_release_note_url || '';

  if (beforeRnUrl && !afterRnUrl) {
    deletedItems.push({
      file_name: initialData?.momenta_release_note_name || 'Momenta Release Note',
      file_size: initialData?.momenta_release_note_size || 0,
      file_type_name: 'Momenta Release Note', change_type: 'deleted',
    });
  }
  if (result.momenta_release_note_name) {
    const rnEntry = progressStore.getProgress(`externalrelease-${result.id}-doc-release_note`);
    const rnItem: UploadedFileItem = {
      file_name: result.momenta_release_note_name, file_size: result.momenta_release_note_size || 0,
      file_type_name: 'Momenta Release Note',
      auto_retry_count: rnEntry?.autoRetryCount ?? 0, manual_retry_count: rnEntry?.manualRetryCount ?? 0,
      upload_started_at: rnEntry?.uploadStartedAt ?? null, upload_finished_at: rnEntry?.uploadFinishedAt ?? null,
      avg_speed_mbps: rnEntry?.avgSpeedMbps ?? null,
    };
    if (!beforeRnUrl || afterRnUrl !== beforeRnUrl) {
      addedItems.push({ ...rnItem, change_type: 'added' });
    } else {
      existingItems.push({ ...rnItem, change_type: 'existing' });
    }
  }

  collectDocItems(
    docList(
      initialData?.momenta_test_report_files,
      initialData?.momenta_test_report_url, initialData?.momenta_test_report_name,
      initialData?.momenta_test_report_size, initialData?.momenta_test_report_checksum,
    ),
    docList(
      result.momenta_test_report_files,
      result.momenta_test_report_url, result.momenta_test_report_name,
      result.momenta_test_report_size, result.momenta_test_report_checksum,
    ),
    `externalrelease-${result.id}-doc-test_report`,
    'Momenta Test Report',
    addedItems, existingItems, deletedItems,
  );

  const allFiles = [...addedItems, ...deletedItems, ...existingItems];
  const nonDeletedFiles = allFiles.filter(f => f.change_type !== 'deleted');

  const uploadRecord = await createUploadRecord({
    project: projectId,
    deliverable_type: 'external_release',
    deliverable_id: result.id,
    deliverable_name: deliverableMeta.name,
    vehicle_model: deliverableMeta.vehicleModel,
    file_count: nonDeletedFiles.length,
    total_size: nonDeletedFiles.reduce((s, f) => s + f.file_size, 0),
    upload_status: 'uploading',
    started_at: startedAt || new Date().toISOString(),
    finished_at: undefined,
    uploaded_files: allFiles,
    description: deliverableMeta.description,
  }).catch(err => { console.warn('[UploadRecord] 上报失败:', err); return null; });

  return { recordId: uploadRecord?.id ?? null, deletedItems, existingItems };
}
